import {
  useEffect, FC, useState, useMemo,
} from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import TextField from '@mui/material/TextField';
import Card from '@mui/material/Card';
import { useTranslation } from 'react-i18next';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { BaseLabel, BaseCheckbox } from '@mgm/common/components/base';
import { CustomTextField } from '@mgm/common/components/form';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { getSelectedCommissionPlan } from '../../../../store/commissions/selectors';
import { commissionsActions } from '../../../../store/commissions/slice';
import { CreateFixedInput } from './CreateFixedInput';
import { CreateStepsInput } from './CreateStepsInput';
import { CreateBonusInput } from './CreateBonusInput';

type TCreatePlanFrom = {
  setIsNextDisabled: (val: boolean) => void;
};

const CreatePlanForm: FC<TCreatePlanFrom> = ({ setIsNextDisabled }) => {
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const dispatch = useAppDispatch();
  const selectedCommissionPlan = useAppSelector(getSelectedCommissionPlan);
  const { t } = useTranslation(['commissions', 'inputs']);
  const [isChecked, setIsChecked] = useState(false);

  const handleCheckboxChange = () => {
    setIsChecked(!isChecked);
  };

  const methods = useForm({
    mode: 'all',
    values: {
      name: selectedCommissionPlan.name || '',
      fixedAmount: selectedCommissionPlan.fixedAmount || '',
    },
  });

  const name = useWatch({
    control: methods.control,
    name: 'name',
  });

  const fixedAmount = useWatch({
    control: methods.control,
    name: 'fixedAmount',
  });

  useEffect(() => {
    if (!name || (selectedCommissionPlan.type === 'Fixed' && !fixedAmount)) {
      setIsNextDisabled(true);
    } else {
      setIsNextDisabled(false);
    }
  }, [name, fixedAmount, selectedCommissionPlan.type, setIsNextDisabled]);

  const handleFormChange = (key: string, val: string | number) => {
    if (key === 'fixedType') {
      const currentValues = methods.getValues();
      methods.reset({
        name: currentValues.name,
        fixedAmount: '',
      });
    }
    dispatch(
      commissionsActions.setSelectedCommissionsPlan({
        ...selectedCommissionPlan,
        [key]: val,
      }),
    );
  };

  const isProductCommission = useMemo(() => selectedCommissionPlan.type === 'Products', [selectedCommissionPlan]);

  return (
    <Container
      maxWidth={false}
      sx={{
        ...(isMobile && {
          px: 0,
        }),
        pt: isMobile ? 3 : 6,
        pb: 8,
      }}
    >
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <Grid
            container
            spacing={4}
            {...(isProductCommission && {
              justifyContent: 'center',
            })}
          >
            <Grid size={{
              xs: 12,
              md: 6,
            }}
            >
              <Card
                sx={{
                  borderRadius: 2,
                  ':hover': {
                    boxShadow: 'rgba(32, 40, 45, 0.08) 0px 2px 14px 0px',
                  },
                }}
              >
                <CardHeader
                  sx={{
                    p: 2,
                  }}
                  title={t('planDetails')}
                  titleTypographyProps={{
                    variant: 'subtitle1',
                  }}
                  {...(!isProductCommission && {
                    subheader: (
                      <>
                        <Typography variant="caption">
                          {selectedCommissionPlan.type === 'Fixed' ? t('inThisSimplifiedModel') : t('addStepsToCreate')}
                          {' '}
                          <br />
                        </Typography>
                        <Typography variant="caption">
                          {selectedCommissionPlan.type === 'Fixed' ? t('determineTheCompensationForEachSale') : t('determineTheCompensation')}
                          {' '}
                          <br />
                        </Typography>
                        <Typography variant="caption">
                          {selectedCommissionPlan.type === 'Fixed' ? t('yourEmployeeWillReceiveCompensation') : t('yourEmployeeWillReceive')}
                          {' '}
                          <br />
                        </Typography>
                      </>
                    ),
                  })}
                />
                <Divider sx={{ borderStyle: 'dashed' }} />
                <CardContent>
                  <CustomTextField
                    onChange={(name) => {
                      handleFormChange('name', name);
                    }}
                    isRequiredField
                    placeholder={t('planName')}
                    labelText={t('name', { ns: 'inputs' })}
                    name="name"
                  />
                  <Box mt={2}>
                    <BaseLabel letterSpacing="-0.5px">{t('description')}</BaseLabel>
                    <TextField
                      value={selectedCommissionPlan.description}
                      onChange={(event) => handleFormChange('description', event.target.value)}
                      multiline
                      fullWidth
                      rows={3}
                    />
                  </Box>
                  {!isProductCommission && (
                  <Box mt={2}>
                    {selectedCommissionPlan.type === 'Fixed' ? (
                      <>
                        <Box pb={2}>
                          <BaseLabel letterSpacing="-0.5px">{t('commissions')}</BaseLabel>
                        </Box>
                        <CreateFixedInput handleFormChange={handleFormChange} />
                      </>
                    ) : (
                      <CreateStepsInput />
                    )}
                  </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
            {!isProductCommission && (
            <Grid size={{
              xs: 12,
              md: 6,
            }}
            >
              <Card
                sx={{
                  borderRadius: 2,
                  ':hover': {
                    boxShadow: 'rgba(32, 40, 45, 0.08) 0px 2px 14px 0px',
                  },
                }}
              >
                <CardHeader
                  sx={{
                    p: 2,
                  }}
                  title={`${t('additionalIncentives')} (${t('bonuses')})`}
                  titleTypographyProps={{
                    variant: 'subtitle1',
                  }}
                  subheader={(
                    <Typography variant="caption">
                      {t('additionalIncentivesAreCalculated')}
                      {' '}
                      {' '}
                      <Typography variant="caption" fontWeight={600}>{t('regardlessOfCommissions')}</Typography>
                      .
                    </Typography>
                  )}
                  action={(
                    <BaseCheckbox handleClick={handleCheckboxChange} value={isChecked} />
                  )}
                />
                <Divider sx={{ borderStyle: 'dashed' }} />
                <CardContent>
                  <CreateBonusInput isChecked={isChecked} />
                </CardContent>
              </Card>
            </Grid>
            )}
          </Grid>
        </Box>
      </FormProvider>
    </Container>
  );
};

export { CreatePlanForm };
